/**
 * Client Detail View Types
 * Feature: 126-client-assignment-views
 * Constitution Version: 1.12.0
 * 
 * Shared TypeScript interfaces for the Client Detail page
 * and its assignment tabs (Capacity, Workspace, SLA).
 */

/** Capacity type values from database */
export type CapacityType = 'Fabric' | 'PowerBI' | 'MsManaged' | 'Unknown';

/** Human-readable labels for capacity types */
export const CAPACITY_TYPE_LABELS: Record<CapacityType, string> = {
  Fabric: 'Fabric',
  PowerBI: 'Power BI',
  MsManaged: 'MS Managed',
  Unknown: 'Unknown',
};

/**
 * Client detail for the page header (FR-001a)
 * Fetched from GET /api/clients/:id
 */
export interface ClientDetail {
  id: string;
  clientAccountId: string;
  clientName: string;
  slaTier: string | null;
  contractValue: number | null;
  isActive: boolean;
  createdAt: string;
  capacityCount: number;
  workspaceCount: number;
  /** Spec 157 US4 — total managed data (bytes) across the client's workspaces */
  totalSizeBytes?: number;
  /** Spec 286 T159 item 32 — how many items totalSizeBytes counts at their last known size (not measured) */
  sizeNotMeasuredCount?: number;
}

/**
 * Capacity assignment with JOINed capacity details (FR-010)
 * Fetched from GET /api/v1/:clientId/capacities
 */
export interface CapacityAssignmentView {
  id: string;
  clientId: string;
  capacityId: string;
  effectiveDate: string;
  endDate: string | null;
  isPrimary: boolean;
  createdAt: string;
  isActive: boolean;
  // JOINed from dbo.capacities
  capacityName: string | null; // null if capacity was deleted (orphaned assignment)
  capacityType: CapacityType | null;
  sku: string | null;
  region: string | null;
  capacityStatus: string | null;
}

/**
 * Workspace assignment with JOINed workspace/capacity details (FR-020)
 * Fetched from GET /api/v1/:clientId/workspaces
 */
export interface WorkspaceAssignmentView {
  id: string;
  clientId: string;
  workspaceId: string;
  effectiveDate: string;
  endDate: string | null;
  createdAt: string;
  isActive: boolean;
  // JOINed from dbo.workspaces
  workspaceName: string | null; // null if workspace was deleted (orphaned assignment)
  workspaceState: string | null;
  // JOINed from dbo.capacities via workspace.capacity_id
  capacityName: string | null;
  capacityType: CapacityType | null;
}

/**
 * SLA assignment with tier details (FR-030)
 * Fetched from GET /api/sla/assignments/client/:clientId/current
 * and GET /api/sla/assignments/client/:clientId/history
 * 
 * Note: Reuses existing ClientSLAAssignmentWithTier from slaService.
 * This interface is for documentation only.
 */
export interface SlaAssignmentView {
  id: string;
  clientId: string;
  slaTierId: string;
  effectiveDate: string;
  expirationDate: string | null;
  effectiveUptimePercent: number;
  effectiveRtoHours: number;
  effectiveRpoHours: number;
  effectivePenaltyPerHourUsd: number;
  createdAt: string;
  updatedAt: string;
  updatedBy: string | null;
  // JOINed from dbo.sla_tiers
  tierName: string;
}

/**
 * Response shape for GET /api/clients/:id
 */
export interface ClientDetailResponse {
  success: boolean;
  data: ClientDetail;
}

/**
 * Response shape for GET /api/v1/:clientId/capacities
 */
export interface CapacityAssignmentsResponse {
  data: CapacityAssignmentView[];
  count: number;
  clientId: string;
}

/**
 * Response shape for GET /api/v1/:clientId/workspaces
 */
export interface WorkspaceAssignmentsResponse {
  data: WorkspaceAssignmentView[];
  count: number;
  clientId: string;
}

/**
 * Tab identifiers for deep-linking (FR-001d)
 */
export type ClientDetailTab = 'capacities' | 'workspaces' | 'sla' | 'blast-radius';

/**
 * Valid tab values for URL query param validation
 */
export const VALID_TABS: ClientDetailTab[] = ['capacities', 'workspaces', 'sla', 'blast-radius'];

/**
 * Check if a string is a valid tab identifier
 */
export function isValidTab(value: string | null): value is ClientDetailTab {
  return value !== null && VALID_TABS.includes(value as ClientDetailTab);
}
